// components/StockResults.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ShowChart as StockIcon,
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Article as WordIcon,
  MoreVert as MoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { SimulationResult, StockForm } from '../simulationTypes';
import {
  formatCurrency,
  formatPercentage,
  exportToPDF,
  exportToExcel,
  exportToWord
} from '../exportCommon';

interface StockResultsProps {
  result: SimulationResult;
  form: StockForm;
  reportRef: React.RefObject<HTMLDivElement>;
  exportMenuAnchor: HTMLElement | null;
  setExportMenuAnchor: (anchor: HTMLElement | null) => void;
}

const StockResults: React.FC<StockResultsProps> = ({
  result,
  form,
  reportRef,
  exportMenuAnchor,
  setExportMenuAnchor
}) => {
  const asset = result.individual_assets[0];
  const analysis = asset.investment_performance_analysis;
  const summary = asset.asset_summary;
  const outcomes = asset.final_outcomes;
  const chartData = asset.distribution_data_for_charts;

  return (
    <Box ref={reportRef}>
      {/* Export Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Stock Analysis Report
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          endIcon={<MoreIcon />}
          onClick={(e) => setExportMenuAnchor(e.currentTarget)}
        >
          Export
        </Button>
      </Box>

      {/* Simulation Summary */}
      <Card elevation={2} sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
              <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <StockIcon sx={{ fontSize: 40 }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h5" fontWeight="bold">
                {summary.asset_name}
              </Typography>
              <Typography variant="subtitle1">
                Simulation ID: {result.simulation_id} | Date:{' '}
                {new Date(result.simulation_date).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                {summary.iterations_completed.toLocaleString()} iterations over{' '}
                {summary.investment_period_years} years
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Key Performance Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card
            elevation={1}
            sx={{
              bgcolor: analysis.expected_returns_p50.annual_return.includes('-')
                ? '#ffebee'
                : '#e8f5e8'
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary">
                Expected Return (P50)
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={
                  analysis.expected_returns_p50.annual_return.includes('-')
                    ? 'error'
                    : 'success.main'
                }
              >
                {analysis.expected_returns_p50.annual_return}
              </Typography>
              <Typography variant="caption">Annual IRR</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={1} sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary">
                Total Profit
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={
                  analysis.expected_returns_p50.total_profit.includes('-')
                    ? 'error'
                    : 'success.main'
                }
              >
                {analysis.expected_returns_p50.total_profit.split('(')[0].trim()}
              </Typography>
              <Typography variant="caption">
                Over {summary.investment_period_years} years
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={1} sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary">
                Success Rate
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {analysis.risk_assessment.probability_of_profit.split(' ')[0]}
              </Typography>
              <Typography variant="caption">Probability of Profit</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={1} sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 0.5, sm: 2 } }}>
              <Typography variant="body2" color="text.secondary">
                Initial Investment
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {formatCurrency(summary.initial_investment)}
              </Typography>
              <Typography variant="caption">Total Capital Invested</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Risk Chips */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Chip
          label={`Best: ${analysis.risk_assessment.best_case}`}
          color="success"
          size="small"
          variant="outlined"
          icon={<CheckCircleIcon />}
          sx={{ mr: 0.5, mb: 0.5 }}
        />
        <Chip
          label={`Worst: ${analysis.risk_assessment.worst_case}`}
          color="error"
          size="small"
          variant="outlined"
          icon={<CancelIcon />}
          sx={{ mr: 0.5, mb: 0.5 }}
        />
        <Chip
          label={`Break Even: ${analysis.expected_returns_p50.break_even}`}
          color="info"
          size="small"
          variant="outlined"
          sx={{ mb: 0.5 }}
        />
      </Box>

      {/* Charts */}
      {chartData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent sx={{ p: { xs: 0, sm: 2 } }}>
                <Typography variant="h6" gutterBottom>
                  Stock Portfolio Value Over Time
                </Typography>
                <Chart
                  options={
                    {
                      chart: { type: 'line', zoom: { enabled: true }, toolbar: { show: false } },
                      stroke: { curve: 'smooth', width: 3 },
                      xaxis: {
                        categories: chartData.asset_value_time_series.years.map(
                          (y: number) => `Year ${y + 1}`
                        ),
                        title: { text: 'Investment Years' }
                      },
                      yaxis: {
                        labels: { formatter: (val: number) => formatCurrency(val) },
                        title: { text: 'Portfolio Value' }
                      },
                      legend: { position: 'top' },
                      colors: ['#f44336', '#2196f3', '#4caf50'],
                      tooltip: { y: { formatter: (val: number) => formatCurrency(val) } }
                    } as ApexOptions
                  }
                  series={[
                    {
                      name: 'Conservative (P10)',
                      data: chartData.asset_value_time_series.P10_values
                    },
                    { name: 'Expected (P50)', data: chartData.asset_value_time_series.P50_values },
                    { name: 'Optimistic (P90)', data: chartData.asset_value_time_series.P90_values }
                  ]}
                  type="line"
                  height={350}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
                <Typography variant="h6" gutterBottom>
                  Annual Dividend Income
                </Typography>
                <Chart
                  options={
                    {
                      chart: { type: 'line', toolbar: { show: false } },
                      stroke: { curve: 'smooth', width: 2 },
                      xaxis: {
                        categories: chartData.cashflow_distribution.years.map(
                          (y: number) => `Year ${y}`
                        ),
                        title: { text: 'Years' }
                      },
                      yaxis: {
                        labels: { formatter: (val: number) => formatCurrency(val) },
                        title: { text: 'Dividend Income' }
                      },
                      legend: { position: 'top' },
                      colors: ['#ff5722', '#03a9f4', '#4caf50'],
                      tooltip: { y: { formatter: (val: number) => formatCurrency(val) } }
                    } as ApexOptions
                  }
                  series={[
                    {
                      name: 'Conservative (P10)',
                      data: chartData.cashflow_distribution.P10_cashflows
                    },
                    { name: 'Expected (P50)', data: chartData.cashflow_distribution.P50_cashflows },
                    {
                      name: 'Optimistic (P90)',
                      data: chartData.cashflow_distribution.P90_cashflows
                    }
                  ]}
                  type="line"
                  height={250}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
                <Typography variant="h6" gutterBottom>
                  Return Distribution
                </Typography>
                <Chart
                  options={
                    {
                      chart: { type: 'bar', toolbar: { show: false } },
                      plotOptions: { bar: { borderRadius: 2 } },
                      xaxis: {
                        categories: chartData.histogram_final_returns.bins.map((bin: number) =>
                          formatCurrency(bin)
                        ),
                        labels: { rotate: -45 }
                      },
                      yaxis: { title: { text: 'Frequency' } },
                      colors: ['#9c27b0'],
                      dataLabels: { enabled: false }
                    } as ApexOptions
                  }
                  series={[
                    { name: 'Frequency', data: chartData.histogram_final_returns.frequencies }
                  ]}
                  type="bar"
                  height={250}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Detailed Analysis */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            Stock Market Analysis
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', height: '100%' }}>
                <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                  {analysis.year_by_year_insights.early_years.description}
                </Typography>
                {analysis.year_by_year_insights.early_years.key_characteristics.map(
                  (char: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <span style={{ marginRight: 8, color: '#1976d2' }}>•</span>
                      {char}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f1f8e9', height: '100%' }}>
                <Typography variant="subtitle1" color="success.main" fontWeight="bold" gutterBottom>
                  {analysis.year_by_year_insights.middle_period.description}
                </Typography>
                {analysis.year_by_year_insights.middle_period.key_characteristics.map(
                  (char: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <span style={{ marginRight: 8, color: '#4caf50' }}>•</span>
                      {char}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', height: '100%' }}>
                <Typography variant="subtitle1" color="warning.main" fontWeight="bold" gutterBottom>
                  {analysis.year_by_year_insights.later_years.description}
                </Typography>
                {analysis.year_by_year_insights.later_years.key_characteristics.map(
                  (char: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <span style={{ marginRight: 8, color: '#ff9800' }}>•</span>
                      {char}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e8' }}>
                <Typography variant="subtitle1" color="success.main" fontWeight="bold" gutterBottom>
                  ✅ Positive Indicators
                </Typography>
                {analysis.actionable_investment_decisions.green_lights.map(
                  (item: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                    >
                      <CheckCircleIcon
                        sx={{ color: 'success.main', mr: 1, mt: 0.2, fontSize: 16 }}
                      />
                      {item}
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff8e1' }}>
                <Typography variant="subtitle1" color="warning.main" fontWeight="bold" gutterBottom>
                  ⚠️ Monitor Closely
                </Typography>
                {analysis.actionable_investment_decisions.red_flags_to_monitor?.length ? (
                  analysis.actionable_investment_decisions.red_flags_to_monitor.map(
                    (item: string, idx: number) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}
                      >
                        <WarningIcon sx={{ color: 'warning.main', mr: 1, mt: 0.2, fontSize: 16 }} />
                        {item}
                      </Typography>
                    )
                  )
                ) : (
                  <Typography variant="body2" color="success.main">
                    No major concerns for diversified stock investment
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Key Takeaway */}
          <Paper elevation={1} sx={{ p: 3, bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              💡 Key Stock Investment Takeaway
            </Typography>
            <Typography variant="body1" color="text.primary" lineHeight={1.6}>
              {analysis.key_takeaway}
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* Final Outcomes Table */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            📊 Final Outcomes Analysis
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Metric</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Conservative (P10)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Expected (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Optimistic (P90)</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Total Asset Value</TableCell>
                  <TableCell align="right">
                    {formatCurrency(outcomes.total_asset_value.P10)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(outcomes.total_asset_value.P50)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(outcomes.total_asset_value.P90)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Net Profit</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: outcomes.total_net_profit.P10 < 0 ? 'error.main' : 'success.main'
                    }}
                  >
                    {formatCurrency(outcomes.total_net_profit.P10)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: outcomes.total_net_profit.P50 < 0 ? 'error.main' : 'success.main'
                    }}
                  >
                    {formatCurrency(outcomes.total_net_profit.P50)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: outcomes.total_net_profit.P90 < 0 ? 'error.main' : 'success.main'
                    }}
                  >
                    {formatCurrency(outcomes.total_net_profit.P90)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Annual IRR</TableCell>
                  <TableCell align="right">
                    {formatPercentage(outcomes.annual_irr_percent.P10)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentage(outcomes.annual_irr_percent.P50)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentage(outcomes.annual_irr_percent.P90)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ROI Percentage</TableCell>
                  <TableCell align="right">{formatPercentage(outcomes.roi_percent.P10)}</TableCell>
                  <TableCell align="right">{formatPercentage(outcomes.roi_percent.P50)}</TableCell>
                  <TableCell align="right">{formatPercentage(outcomes.roi_percent.P90)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Benchmark Comparisons */}
      <Card elevation={2} sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
            📊 Benchmark Comparisons
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`vs Term Deposits: ${analysis.comparison_benchmarks.against_alternatives.term_deposits}`}
                color="info"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`vs Bonds: ${analysis.comparison_benchmarks.against_alternatives.bonds}`}
                color="info"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Chip
                label={`vs REITs: ${analysis.comparison_benchmarks.against_alternatives.reit}`}
                color="info"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Risk-Adjusted Assessment:</strong>{' '}
            {analysis.comparison_benchmarks.risk_adjusted_assessment}
          </Typography>
        </CardContent>
      </Card>

      {/* Yearly Projections Table */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            📅 Detailed Yearly Projections
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Year</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Asset Value (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Annual Income (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Annual Expenses (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Net Cash Flow (P50)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Cumulative Profit (P50)</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asset.yearly_projections.map((projection: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{projection.year}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(projection.asset_value.P50)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(projection.annual_income.P50)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(projection.annual_expenses.P50)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: projection.net_cashflow.P50 < 0 ? 'error.main' : 'success.main'
                      }}
                    >
                      {formatCurrency(projection.net_cashflow.P50)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: projection.cumulative_profit.P50 < 0 ? 'error.main' : 'success.main'
                      }}
                    >
                      {formatCurrency(projection.cumulative_profit.P50)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => exportToPDF(result, reportRef, form.assetName, setExportMenuAnchor)}
        >
          <PdfIcon sx={{ mr: 1 }} /> Export as PDF
        </MenuItem>
        <MenuItem onClick={() => exportToExcel(result, form, setExportMenuAnchor, 'stock')}>
          <ExcelIcon sx={{ mr: 1 }} /> Export as Excel
        </MenuItem>
        <MenuItem onClick={() => exportToWord(result, form, setExportMenuAnchor, 'stock')}>
          <WordIcon sx={{ mr: 1 }} /> Export as Word
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default StockResults;
